import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import type { CheckedState } from "@radix-ui/react-checkbox";
import { forwardRef, useId } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export type CheckboxCheckedState = CheckedState;

export interface CheckboxProps extends Omit<
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  "children"
> {
  label?: ReactNode;
  description?: string;
  icon?: ReactNode;
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  function Checkbox(
    {
      className,
      description,
      disabled = false,
      icon = <CheckIcon />,
      id,
      label,
      ...props
    },
    ref,
  ) {
    const generatedId = useId();
    const checkboxId = id ?? generatedId;
    const descriptionId = description ? `${checkboxId}-description` : undefined;

    return (
      <label
        htmlFor={checkboxId}
        className={twMerge(
          "group inline-flex items-center gap-2",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          className,
        )}
      >
        <CheckboxPrimitive.Root
          ref={ref}
          id={checkboxId}
          aria-describedby={descriptionId}
          disabled={disabled}
          className={twMerge(
            "bg-surface text-content-on-action relative inline-flex size-4 shrink-0 items-center justify-center rounded border p-0 transition duration-200 focus-visible:outline-hidden",
            disabled
              ? "border-content-disabled bg-surface-muted text-content-disabled data-[state=checked]:border-action data-[state=checked]:bg-action data-[state=checked]:text-content-on-action shadow-none data-[state=checked]:opacity-65"
              : "data-[state=checked]:border-action data-[state=checked]:bg-action data-[state=unchecked]:hover:border-foreground/70 border-[#D0D5DD] hover:shadow-[0_0_0_4px_rgba(15,23,42,0.06)] focus-visible:shadow-[0_0_0_4px_rgba(15,23,42,0.08)] data-[state=checked]:hover:shadow-[0_0_0_4px_rgba(24,132,249,0.16)] data-[state=checked]:focus-visible:shadow-[0_0_0_4px_rgba(24,132,249,0.18)]",
          )}
          {...props}
        >
          <CheckboxPrimitive.Indicator className="invisible absolute inset-0 flex items-center justify-center data-[state=checked]:visible">
            {icon}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>

        {(label || description) && (
          <span className="min-w-0 flex-1">
            {label && (
              <span
                className={twMerge(
                  "block text-[13px] leading-normal font-medium text-[#374151]",
                  disabled
                    ? "text-content-disabled"
                    : "text-foreground group-hover:text-[#1884F9]",
                )}
              >
                {label}
              </span>
            )}
            {description && (
              <span
                id={descriptionId}
                className={twMerge(
                  "mt-1 block text-[13px]",
                  disabled ? "text-content-disabled" : "text-muted",
                )}
              >
                {description}
              </span>
            )}
          </span>
        )}
      </label>
    );
  },
);
